import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  Copy,
  Printer,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import TwoFactorEnrollment from "@/components/TwoFactorEnrollment";
import TrustedDevicesList from "@/components/TrustedDevicesList";

export default function TwoFactorSettingsPanel() {
  const { user, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [enrollmentOpen, setEnrollmentOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [trustedDevicesOpen, setTrustedDevicesOpen] = useState(false);

  function refreshUser() {
    queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading...</p>;
  }

  const enabled = (user as any)?.twoFactorEnabled === true;

  return (
    <div>
      <h3 className="font-medium flex items-center gap-2">
        {enabled ? (
          <ShieldCheck className="h-4 w-4 text-green-600" />
        ) : (
          <ShieldAlert className="h-4 w-4 text-yellow-600" />
        )}
        Two-Factor Authentication
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        {enabled
          ? "Two-factor authentication is enabled on your account."
          : "Add an extra layer of security with an authenticator app."}
      </p>

      {!enabled && (
        <Button variant="outline" onClick={() => setEnrollmentOpen(true)}>
          Enable 2FA
        </Button>
      )}

      {enabled && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setRegenOpen(true)}>
              <KeyRound className="h-4 w-4 mr-1" /> Regenerate backup codes
            </Button>
            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700"
              onClick={() => setDisableOpen(true)}
            >
              Disable 2FA
            </Button>
          </div>

          <button
            type="button"
            className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 hover:underline"
            onClick={() => setTrustedDevicesOpen((v) => !v)}
          >
            {trustedDevicesOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Trusted devices
          </button>
          {trustedDevicesOpen && (
            <div className="pl-5">
              <TrustedDevicesList />
            </div>
          )}
        </div>
      )}

      <TwoFactorEnrollment
        open={enrollmentOpen}
        onOpenChange={setEnrollmentOpen}
        onComplete={() => {
          toast({
            title: "2FA enabled",
            description: "Two-factor authentication is now protecting your account.",
          });
          refreshUser();
        }}
      />

      <DisableDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        onSuccess={() => {
          toast({
            title: "2FA disabled",
            description: "Two-factor authentication has been turned off.",
          });
          refreshUser();
        }}
      />

      <RegenDialog
        open={regenOpen}
        onOpenChange={setRegenOpen}
        onSuccess={() => {
          toast({
            title: "Backup codes regenerated",
            description: "Your previous codes are no longer valid.",
          });
        }}
      />
    </div>
  );
}

// ---------- Disable dialog (TOTP step-up + AlertDialog confirmation) ----------

function DisableDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSuccess: () => void;
}) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (code.length !== 6 || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      // M4: step-up first, then the actual disable (no body).
      const stepup = await fetch("/api/auth/2fa/stepup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });
      if (!stepup.ok) {
        if (stepup.status === 429) {
          const data = await stepup.json().catch(() => ({}));
          setError(
            data.lockedUntil
              ? `Too many attempts. Try again after ${new Date(data.lockedUntil).toLocaleTimeString()}.`
              : "Too many attempts. Try again later."
          );
        } else {
          setError("Invalid code. Try again.");
          setCode("");
        }
        return;
      }
      const res = await fetch("/api/auth/2fa/disable", {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        onOpenChange(false);
        setCode("");
        onSuccess();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Failed to disable 2FA.");
      }
    } catch {
      setError("Network error. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) {
          setCode("");
          setError("");
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Disable Two-Factor Authentication?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes 2FA protection and revokes all trusted devices. Enter
            your current authenticator code to confirm.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="flex justify-center py-2">
          <InputOTP maxLength={6} value={code} onChange={setCode} autoFocus>
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
        {error && <div className="text-red-500 text-sm text-center">{error}</div>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={submit}
            disabled={code.length !== 6 || submitting}
            className="bg-red-600 hover:bg-red-700"
          >
            {submitting ? "Disabling..." : "Disable 2FA"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ---------- Regen dialog (TOTP step-up → new codes display) ----------

function RegenDialog({
  open,
  onOpenChange,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState<"verify" | "codes">("verify");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [newCodes, setNewCodes] = useState<string[]>([]);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  function reset() {
    setStep("verify");
    setCode("");
    setError("");
    setNewCodes([]);
    setSavedConfirmed(false);
  }

  async function submit() {
    if (code.length !== 6 || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      // M4: step-up first, then the regenerate call (no body).
      const stepup = await fetch("/api/auth/2fa/stepup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code }),
      });
      if (!stepup.ok) {
        if (stepup.status === 429) {
          const data = await stepup.json().catch(() => ({}));
          setError(
            data.lockedUntil
              ? `Too many attempts. Try again after ${new Date(data.lockedUntil).toLocaleTimeString()}.`
              : "Too many attempts. Try again later."
          );
        } else {
          setError("Invalid code. Try again.");
          setCode("");
        }
        return;
      }
      const res = await fetch("/api/auth/2fa/backup-codes/regenerate", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Failed to regenerate codes.");
        return;
      }
      const data = await res.json();
      setNewCodes(data.backupCodes);
      setStep("codes");
      onSuccess();
    } catch {
      setError("Network error. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function copyAll() {
    navigator.clipboard
      .writeText(newCodes.join("\n"))
      .then(() => toast({ title: "Copied", description: "Backup codes copied." }))
      .catch(() => toast({ title: "Copy failed", variant: "destructive" }));
  }

  function printCodes() {
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html><head><title>Administr 2FA Backup Codes</title>
      <style>body{font-family:monospace;padding:24px}h1{font-family:sans-serif}.code{font-size:18px;letter-spacing:2px;padding:4px 0}</style>
      </head><body>
        <h1>Administr — 2FA Backup Codes</h1>
        <p>Each code works once.</p>
        ${newCodes.map((c) => `<div class="code">${c.slice(0, 5)}-${c.slice(5)}</div>`).join("")}
        <p style="margin-top:32px;font-family:sans-serif;color:#666">Generated: ${new Date().toLocaleString()}</p>
      </body></html>
    `);
    win.document.close();
    win.print();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && step === "codes" && !savedConfirmed) return; // block accidental close
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        onPointerDownOutside={(e) => {
          if (step === "codes" && !savedConfirmed) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (step === "codes" && !savedConfirmed) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Regenerate Backup Codes</DialogTitle>
          <DialogDescription>
            {step === "verify"
              ? "Enter your current authenticator code. This will invalidate all previous backup codes."
              : "Save these new codes. The previous ones no longer work."}
          </DialogDescription>
        </DialogHeader>

        {step === "verify" && (
          <div className="space-y-3">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={code} onChange={setCode} autoFocus>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {error && <div className="text-red-500 text-sm text-center">{error}</div>}
          </div>
        )}

        {step === "codes" && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 dark:bg-gray-800 p-3 rounded">
              {newCodes.map((c) => (
                <div key={c}>{c.slice(0, 5)}-{c.slice(5)}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={copyAll}>
                <Copy className="h-4 w-4 mr-1" /> Copy all
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={printCodes}>
                <Printer className="h-4 w-4 mr-1" /> Print
              </Button>
            </div>
            <label className="flex items-center gap-2 text-sm pt-2">
              <Checkbox
                checked={savedConfirmed}
                onCheckedChange={(v) => setSavedConfirmed(v === true)}
              />
              I have saved these codes in a safe place
            </label>
          </div>
        )}

        <DialogFooter>
          {step === "verify" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={code.length !== 6 || submitting}
                className="bg-[#1737A2] hover:bg-[#102366]"
              >
                {submitting ? "Generating..." : "Generate new codes"}
              </Button>
            </>
          )}
          {step === "codes" && (
            <Button
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              disabled={!savedConfirmed}
              className="bg-[#1737A2] hover:bg-[#102366]"
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
